<template>
  <div class="screen_resume">
    <div class="no_print">Please use the PRINT/PDF button for a printable and exportable version of this resume.</div>
    <div class="content">
      <div :class="['challenge_layer', { active : ! resumeModel }]">
        <div :class="['challenge', { disabled : formDisabled }]">
          <h2>Access Code</h2>
          <div class="input_wrapper">
            <input type="text"
                   autofocus="yes"
                   value="" maxlength="50"
                   v-model="accessCode"
                   ref="codeInput"
                   @input="validate"
                   @keypress="keyPressAccess"
                   :disabled="formDisabled"/>
          </div>
          <div class="button_wrapper">
            <button class="submit"
                    type="submit"
                    :disabled="submitDisabled"
                    @click="checkAccess">Submit</button>
          </div>
          <div class="progress"><div class="puck"></div></div>
          <div class="error">{{ errorMessage }}</div>
        </div>
      </div>
      <div :class="['resume_layer', { active : resumeModel }]">
        <div :class="['resume_content', { live : resumeLive }]" v-if="resumeModel">
          <div class="controls">
            <div class="print" title="Print / PDF" @click="print">
              <div class="icon"></div>
              <div class="label">Print / PDF</div>
            </div>
          </div>
          <section class="profile">
            <h2 class="letter_zoom">{{ resumeModel.fullName() }}</h2>
            <div class="address letter_zoom">{{ resumeModel.fullAddress() }}</div>
            <div class="contact">
              <div class="email letter_zoom">{{ resumeModel.primaryEmail() }}</div>
              <div class="phone letter_zoom">{{ resumeModel.primaryPhone() }}</div>
            </div>
            <div class="urls">
              <div class="url"
                   v-for="url in resumeModel.normalUrls()"
                   :key="url.value"><a :href="url.value" target="_blank">{{ url.baseUrl }}</a></div>
            </div>
          </section>
          <section class="objective">
            <h2 class="letter_zoom">Objective</h2>
            <p v-for="(fragment, i) in objectiveMarkup"
               :key="i"
               class="md"
               v-html="fragment"></p>
          </section>
          <section class="education">
            <h2 class="letter_zoom">Education</h2>
            <education v-for="(item, i) in resumeModel.education" :key="i" :model="item"></education>
          </section>
          <section class="skills">
            <h2 class="letter_zoom">Skills</h2>
            <div class="skill_columns">
              <div class="skill_column" v-for="n in skillColCt" :key="n">
                <skill-type v-for="(item, i) in skillTypeBuckets[n - 1]"
                            :key="i"
                            :model="item"></skill-type>
              </div>
            </div>
          </section>
          <section class="projects">
            <h2 class="letter_zoom">Independent Projects</h2>
            <indy-project v-for="(item, i) in resumeModel.projects" :key="i" :model="item"></indy-project>
          </section>
          <section class="experience">
            <h2 class="letter_zoom">Work Experience</h2>
            <experience v-for="(item, i) in resumeModel.experience" :key="i" :model="item"></experience>
          </section>
        </div>
      </div>
    </div>
  </div>
</template>

<script>

import axios from 'axios';

import Education from '@/components/screen-resume/education';
import Experience from '@/components/screen-resume/experience';
import IndyProject from '@/components/screen-resume/project';
import SkillType from '@/components/screen-resume/skill-type';
import promiseUtil from '@/util/promise';
import MarkdownMixin from '@/mixins/markdown';
import Resume from '@/model/resume';

export default {
    name       : "ScreenResume",
    components : { Education, Experience, IndyProject, SkillType },
    mixins     : [ MarkdownMixin ],
    props      : {
        code : {
            type    : String,
            default : ""
        }
    },
    data() {
        return {
            accessCode       : '',
            storageKey       : "access_code",
            expiration       : 1 * 60 * 60 * 1000, // 1 hour
            isChallengeValid : false,
            working          : false,
            errorMessage     : '',
            resumeModel      : null,
            resumeLive       : false,
            skillColCt       : 2
        };
    },
    computed : {
        submitDisabled() {
            return this.working || ! this.isChallengeValid || this.resumeModel;
        },
        formDisabled() {
            return this.working || this.resumeModel;
        },
        objectiveMarkup() {
            const model = this.resumeModel;

            if (model) {
                return model.data.objective.map(text => {
                    return this.markdown.renderInline(text.trim());
                });
            }

            return [];
        },
        skillTypeBuckets() {
            const ct      = this.skillColCt;
            const buckets = Array(ct).fill().map(() => []);
            const fills   = Array(ct).fill().map(() => 0);

            if (this.resumeModel) {
                this.resumeModel.orderedSkillTypes().forEach((model) => {
                    let idx = 0;
                    let max = fills[idx];

                    fills.forEach((value, i) => {
                        if (value < max) {
                            max = value;
                            idx = i;
                        }
                    });

                    buckets[idx].push(model);
                    fills[idx] += (model.findSkillList().length + 1);
                });
            }

            return buckets;
        }
    },
    mounted() {
        this.accessCode = this.obtainCode();
        this.validate();
        if (this.isChallengeValid) {
            this.checkAccess();
        }
    },
    methods : {
        obtainCode() {
            if (this.code) {
                this.saveCodeInStorage(this.code);
                return this.code;
            };

            return this.getCodeFromStorage() || "";
        },
        getCodeFromStorage() {
            let code = "";

            try {
                const storage = window.sessionStorage;
                const stored  = JSON.parse(
                    storage[this.storageKey] || null
                );
                if (stored) {
                    const exp = stored.expires;
                    const now = (new Date()).getTime();
                    if (exp && exp > now) {
                        code = stored.code || "";
                    }
                }

                if (! code) {
                    delete storage[this.storageKey];
                }
            } catch (e) {
            }

            return code;
        },
        saveCodeInStorage(code) {
            try {
                const storage = window.sessionStorage;

                if (code) {
                    const now     = new Date();
                    const data    = {
                        code    : code,
                        expires : now.getTime() + this.expiration
                    };
                    storage[this.storageKey] = JSON.stringify(data);
                } else {
                    delete storage[this.storageKey];
                }
            } catch (e) {
            }
        },
        validate() {
            let valid = true;

            if (this.accessCode === "") {
                valid = false;
            }

            this.isChallengeValid = valid;

            return this.isChallengeValid;
        },
        keyPressAccess(event) {
            if (! this.isChallengeValid) {
            }

            if (event.key.toLowerCase() === 'enter') {
                this.checkAccess();
            }
        },
        checkAccess() {
            this.$refs.codeInput.blur();

            this.working = true;
            this.fetchData();
        },
        fetchData() {
            this.resumeLive   = false;
            this.errorMessage = '';

            const params = { access : this.accessCode };

            const p1 = axios({
                method       : 'get',
                timeout      : 5000,
                url          : '/ws/resume/',
                responseType : 'json',
                params,
                headers      : {
                    'Content-Type' : "application/json"
                }
            });
            const p2 = promiseUtil.wait(1000);

            Promise.all([p1, p2]).then(
                this.loadResponse.bind(this, params),
                this.errorResponse.bind(this, params)
            ).finally(() => {
                this.working = false;
                setTimeout(() => { this.resumeLive = true; });
            });
        },
        loadResponse(params, results) {
            const response = results[0];

            this.resumeModel = new Resume(response.data);

            if (params && params.access) {
                this.saveCodeInStorage(params.access);
            }
        },
        errorResponse(params, error) {
            let msg = "Error fetching resume data";
            if (error.response && error.response.data) {
                msg = error.response.data.message;
            }

            this.errorMessage = msg;

            setTimeout(() => { this.$refs.codeInput.focus(); });

            if (error.response.status === 401) {
                this.saveCodeInStorage(null);
            }
        },
        print() {
            window.open(
                `/resume?format=print&print=1&access=${this.accessCode}`
            );
        }
    }
};

</script>

<style scoped>

.screen_resume {
}

.no_print {
    display:none;
    font-size:40px;
    line-height:80px;
    text-align:center;
    background-color:rgba(var(--core-black-rgb), 0.25);
    backdrop-filter:blur(10px);
    -webkit-backdrop-filter:blur(10px);
    color:var(--core-white);
    padding:25px;
    margin-top:50px;
}

.content {
    position:relative;
}

.challenge_layer,
.resume_layer {
    position:absolute;
    top:300px;
    left:0px;
    width:100%;
    height:100%;
    display:flex;
    opacity:0;
    z-index:1;
    flex-direction:column;
    justify-content:flex-start;
    align-items:center;
    transition:top 2000ms, opacity 2000ms;
}

.challenge_layer.active,
.resume_layer.active {
    z-index:2;
    top:0px;
    opacity:1;
}

.challenge {
    background-color:rgba(0, 0, 0, 0.2);
    backdrop-filter:blur(5px);
    -webkit-backdrop-filter:blur(5px);
    padding:50px;
    margin-top:100px;
}

.challenge h2 {
    color:var(--core-white);
    font-size:20px;
    font-weight:200;
    text-align:center;
    margin-top:0px;
    padding-top:0px;
}

.challenge .input_wrapper {
    background-image:-webkit-linear-gradient(to right, var(--dark-orange), var(--main-orange), var(--dark-orange));
    background-image:-moz-linear-gradient(to right, var(--dark-orange), var(--main-orange), var(--dark-orange));
    background-image:-ms-linear-gradient(to right, var(--dark-orange), var(--main-orange), var(--dark-orange));
    background-image:linear-gradient(to right, var(--dark-orange), var(--main-orange), var(--dark-orange));
    padding:3px;
    display:inline-block;
    margin-bottom:20px;
    opacity:1;
    transition:opacity 500ms;
}

.challenge .button_wrapper {
    margin-bottom:20px;
}

.challenge.disabled .input_wrapper {
    opacity:0.5;
}

.challenge input {
    background-color:rgba(0, 0, 0, 0.75);
    background-color:var(--deep-gray);
    outline:0px;
    margin:0px;
    padding:5px;
    border:0px;
    color:#FFFFFF;
    font-size:32px;
    font-weight:200;
    width:300px;
}

.challenge button.submit {
    width:calc(100% - 2px);
    background-image:-webkit-linear-gradient(to right, var(--dark-orange), var(--main-orange), var(--dark-orange));
    background-image:-moz-linear-gradient(to right, var(--dark-orange), var(--main-orange), var(--dark-orange));
    background-image:-ms-linear-gradient(to right, var(--dark-orange), var(--main-orange), var(--dark-orange));
    background-image:linear-gradient(to right, var(--dark-orange), var(--main-orange), var(--dark-orange));
    color:#FFFFFF;
    font-size:24px;
    font-weight:200;
    border:1px solid var(--main-orange);
    padding:5px;
    transition:opacity 500ms;
    cursor:pointer;
}

.challenge button.submit:disabled {
    opacity:0.5;
    cursor:auto;
}

.challenge .progress {
    opacity:0;
    width:100%;
    height:4px;
    background-color:var(--core-black);
    transition:opacity 500ms;
    margin-bottom:20px;
    text-align:left;
}

.challenge.disabled .progress {
    opacity:1;
}

.challenge .error {
    font-size:24px;
    color:#FF0000;
    opacity:0;
    text-align:center;
    padding:5px 0px;
    transition:opacity 400ms;
}

.challenge:not(.disabled) .error:not(:empty) {
    opacity:1;
}

.challenge .progress > .puck {
    background-color:var(--pale-orange);
    box-shadow:0px 0px 10px 5px var(--main-orange);
    display:inline-block;
    width:30px;
    height:100%;
    animation-name:progress-puck;
    animation-duration:2000ms;
    animation-timing-function:ease-in-out;
    animation-iteration-count:infinite;
}

.resume_content {
    padding-top:100px;
    width:800px;
}

.resume_content .controls {
    text-align:center;
}

.resume_content .controls > * {
    text-align:center;
    transition:transform 100ms, background-color 1000ms;
    display:inline-block;
    padding:10px 10px 5px;
    -webkit-backdrop-filter:blur(10px);
    backdrop-filter:blur(10px);
    -webkit-user-select:none;
    -moz-user-select:none;
    -ms-user-select:none;
    user-select:none;
}

.resume_content .controls > *:hover {
    background-color:rgba(0, 0, 0, 0.35);
}

.resume_content .controls > *:active {
    transform:translateY(1px);
}

.resume_content .controls > .print {
    cursor:pointer;
}

.resume_content .controls > .print > .icon {
    height:50px;
    width:50px;
    margin:auto;
    background-image:url('/images/icons/printer.svg');
    background-size:auto 50px;
    background-repeat:no-repeat;
}

.resume_content .controls > .print > .label {
    text-transform:uppercase;
    font-size:14px;
    color:#FF8000;
    padding:5px 0px 0px;
    text-align:center;
}

.resume_content section {
    background-color:rgba(var(--core-black-rgb), 0.2);
    backdrop-filter:blur(10px);
    -webkit-backdrop-filter:blur(10px);
    padding:25px;
    margin:20px 0px 40px;
    color:var(--core-white);
    position:relative;
}

.resume_content section:before {
    position:absolute;
    content:"";
    top:-1px;
    left:0px;
    width:100%;
    height:1px;
    background-image:-webkit-linear-gradient(to right, rgba(var(--main-orange-rgb), 0), rgba(var(--main-orange-rgb), 1), rgba(var(--main-orange-rgb), 0));
    background-image:-moz-linear-gradient(to right, rgba(var(--main-orange-rgb), 0), rgba(var(--main-orange-rgb), 1), rgba(var(--main-orange-rgb), 0));
    background-image:-ms-linear-gradient(to right, rgba(var(--main-orange-rgb), 0), rgba(var(--main-orange-rgb), 1), rgba(var(--main-orange-rgb), 0));
    background-image:linear-gradient(to right, rgba(var(--main-orange-rgb), 0), rgba(var(--main-orange-rgb), 1), rgba(var(--main-orange-rgb), 0));
}

.resume_content section:after {
    position:absolute;
    content:"";
    top:-5px;
    left:20%;
    width:60%;
    height:10px;
    background-image:-webkit-linear-gradient(to right, rgba(var(--pale-orange-rgb), 0), rgba(var(--pale-orange-rgb), 1), rgba(var(--pale-orange-rgb), 0));
    background-image:-moz-linear-gradient(to right, rgba(var(--pale-orange-rgb), 0), rgba(var(--pale-orange-rgb), 1), rgba(var(--pale-orange-rgb), 0));
    background-image:-ms-linear-gradient(to right, rgba(var(--pale-orange-rgb), 0), rgba(var(--pale-orange-rgb), 1), rgba(var(--pale-orange-rgb), 0));
    background-image:linear-gradient(to right, rgba(var(--pale-orange-rgb), 0), rgba(var(--pale-orange-rgb), 1), rgba(var(--pale-orange-rgb), 0));
    filter:blur(7px);
    -webkit-filter:blur(7px);
}

.resume_content section p {
    font-size:20px;
}

.resume_content section > h2 {
    margin:0px 0px 25px;
    font-weight:normal;
    font-size:24px;
    color:var(--soft-orange);
    text-transform:uppercase;
    text-align:center;
}

.resume_content section.profile > h2 {
    color:var(--core-white);
}

.resume_content section.profile > .address {
    font-size:16px;
    text-align:center;
    text-transform:uppercase;
    margin-bottom:10px;
}

.resume_content section.profile > .contact {
    display:flex;
    justify-content:center;
    align-items:flex-end;
}

.resume_content section.profile > .contact > * {
    font-size:12px;
    text-transform:uppercase;
    padding:0px 10px;
}

.resume_content section.profile > .urls {
    display:flex;
    flex-direction:column;
    align-items:center;
    gap:1em;
    font-size:14px;
    margin-top:20px;
}

.resume_content section.profile > .urls > .url > a {
    text-decoration:none;
    color:var(--light-orange);
    transition:color 250ms;
}

.resume_content section.profile > .urls > .url > a:hover {
    color:var(--soft-orange);
}

.skill_columns {
    display:flex;
    flex-wrap:nowrap;
    justify-content:space-around;
    align-items:flex-start;
}

.letter_zoom {
    letter-spacing:normal;
    transition:letter-spacing 3000ms;
}

.live .letter_zoom {
    letter-spacing:0.35em;
}


@keyframes progress-puck {
    0% { transform:translateX(0px); }
    50% { transform:translateX(290px); }
    100% { transform:translateX(0px); }
}

@media only screen and (max-width:400px) {
    .challenge > .input > input {
        width:220px;
    }
}

@media only screen and (max-height:700px) {
    .challenge {
        margin-top:50px;
    }
}

@media print {
    .screen_resume {
        width:100%;
    }

    .no_print {
        display:block;
    }

    .content {
        display:none;
    }
}

</style>

<style>

p.md {
    margin-top:0em;
    padding-top:0em;
}

p.md + p.md {
    margin-top:1em;
    padding-top:1em;
}

</style>
